import { buildChildrenByParentId } from "@/lib/documentGroupTree";

// Compose/parse the Advanced Search bar string. Canonical server parser:
// app/services/file_center/exact_match_query.rb

const EXACT_SEARCH_MIN_LENGTH = 3;
// Canonical server cap: FileCenter::ExactMatchQuery::MAX_TERMS_PER_GROUP
const EXACT_SEARCH_MAX_TERMS = 8;

export type ExactSearchFields = {
  all: string;
  phrase: string;
  any: string;
  none: string;
};

export const EMPTY_EXACT_SEARCH_FIELDS: ExactSearchFields = {
  all: "",
  phrase: "",
  any: "",
  none: "",
};

type TokenType = "word" | "phrase" | "or" | "minus";

type Token = {
  type: TokenType;
  value: string;
};

function keepLong(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length >= EXACT_SEARCH_MIN_LENGTH ? trimmed : null;
}

function keepLongList(values: string[]): string[] {
  return values
    .map(keepLong)
    .filter((value): value is string => value != null)
    .slice(0, EXACT_SEARCH_MAX_TERMS);
}

function normalizeWhitespace(raw: string): string {
  return raw.replace(/\p{White_Space}+/gu, " ").trim();
}

function tokenizeStatement(raw: string): Token[] {
  const tokens: Token[] = [];
  const source = normalizeWhitespace(raw);
  let i = 0;

  const skipSpace = () => {
    while (i < source.length && /\s/.test(source[i])) i += 1;
  };

  while (i < source.length) {
    skipSpace();
    if (i >= source.length) break;

    if (source.startsWith('-"', i)) {
      const end = source.indexOf('"', i + 2);
      if (end === -1) {
        tokens.push({ type: "minus", value: source.slice(i + 2) });
        break;
      }
      tokens.push({ type: "minus", value: source.slice(i + 2, end) });
      i = end + 1;
      continue;
    }

    if (source[i] === "-" && i + 1 < source.length && source[i + 1] !== " ") {
      const start = i + 1;
      i += 1;
      while (i < source.length && !/\s/.test(source[i])) i += 1;
      tokens.push({ type: "minus", value: source.slice(start, i).replace(/^"|"$/g, "") });
      continue;
    }

    if (source[i] === '"') {
      const end = source.indexOf('"', i + 1);
      if (end === -1) {
        tokens.push({ type: "phrase", value: source.slice(i + 1) });
        break;
      }
      tokens.push({ type: "phrase", value: source.slice(i + 1, end) });
      i = end + 1;
      continue;
    }

    if (source.startsWith("OR", i) && (i + 2 === source.length || /\s/.test(source[i + 2]))) {
      tokens.push({ type: "or", value: "OR" });
      i += 2;
      continue;
    }

    const start = i;
    while (i < source.length && !/\s/.test(source[i])) i += 1;
    tokens.push({ type: "word", value: source.slice(start, i) });
  }

  return tokens;
}

function tokenizeFieldTerms(raw: string): string[] {
  const tokens: string[] = [];
  const source = normalizeWhitespace(raw);
  let i = 0;

  const skipSpace = () => {
    while (i < source.length && /\s/.test(source[i])) i += 1;
  };

  while (i < source.length) {
    skipSpace();
    if (i >= source.length) break;

    if (source[i] === '"') {
      const end = source.indexOf('"', i + 1);
      if (end === -1) {
        tokens.push(source.slice(i + 1));
        break;
      }
      tokens.push(source.slice(i + 1, end));
      i = end + 1;
      continue;
    }

    const start = i;
    while (i < source.length && !/\s/.test(source[i])) i += 1;
    tokens.push(source.slice(start, i));
  }

  return tokens;
}

function tokenizeField(raw: string): string[] {
  return keepLongList(tokenizeFieldTerms(raw));
}

function quoteTerm(value: string): string {
  return /^[-"]/.test(value) || /\s/.test(value) ? `"${value.replace(/"/g, "")}"` : value;
}

function quotedPhrasesOnly(raw: string): string[] | null {
  const tokens = tokenizeStatement(raw);
  if (tokens.length === 0 || tokens.some((token) => token.type !== "phrase")) return null;
  return tokens.map((token) => token.value);
}

export function splitExactWords(raw: string): string[] {
  return tokenizeStatement(raw)
    .filter((token) => token.type === "word" || token.type === "phrase")
    .map((token) => token.value.trim())
    .filter((value) => value.length > 0);
}

export function splitExactPhrases(raw: string): string[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const quoted = quotedPhrasesOnly(trimmed);
  if (quoted) return quoted.map((value) => value.trim()).filter((value) => value.length > 0);
  const phrase = trimmed.replace(/"/g, "").trim();
  return phrase ? [phrase] : [];
}

export function joinExactWords(tokens: string[]): string {
  return tokens.map(quoteTerm).join(" ");
}

export function joinExactPhrases(tokens: string[]): string {
  if (tokens.length <= 1) return (tokens[0] ?? "").replace(/"/g, "");
  return tokens.map((value) => `"${value.replace(/"/g, "")}"`).join(" ");
}

export function composeExactSearch(fields: ExactSearchFields): string {
  const parts: string[] = [];
  const allWords = tokenizeField(fields.all);
  if (allWords.length > 0) parts.push(allWords.map(quoteTerm).join(" "));

  for (const phrase of keepLongList(splitExactPhrases(fields.phrase))) {
    parts.push(`"${phrase.replace(/"/g, "")}"`);
  }

  const anyWords = tokenizeField(fields.any);
  if (anyWords.length > 0) parts.push(anyWords.map(quoteTerm).join(" OR "));

  for (const word of tokenizeField(fields.none)) {
    parts.push(`-${quoteTerm(word)}`);
  }

  return parts.join(" ");
}

export type ExactSearchGroups = {
  all: string[];
  phrases: string[];
  any: string[];
  none: string[];
};

export function parseExactSearchGroups(raw: string): ExactSearchGroups {
  const tokens = tokenizeStatement(raw);
  const noneWords: string[] = [];
  const body: Token[] = [];
  for (const token of tokens) {
    if (token.type === "minus") noneWords.push(token.value);
    else body.push(token);
  }

  const used = body.map(() => false);
  const anyWords: string[] = [];
  body.forEach((token, i) => {
    if (token.type === "or") return;
    const inOr = (i > 0 && body[i - 1].type === "or") || (i + 1 < body.length && body[i + 1].type === "or");
    if (!inOr) return;
    anyWords.push(token.value);
    used[i] = true;
  });

  const phrases: string[] = [];
  const allWords: string[] = [];
  body.forEach((token, i) => {
    if (used[i] || token.type === "or") return;
    if (token.type === "phrase") phrases.push(token.value);
    else allWords.push(token.value);
  });

  return {
    all: keepLongList(allWords),
    phrases: keepLongList(phrases),
    any: keepLongList(anyWords),
    none: keepLongList(noneWords),
  };
}

export function parseExactSearch(raw: string): ExactSearchFields {
  const groups = parseExactSearchGroups(raw);

  return {
    all: groups.all.join(" "),
    phrase: joinExactPhrases(groups.phrases),
    any: groups.any.map(quoteTerm).join(" "),
    none: groups.none.map(quoteTerm).join(" "),
  };
}

export type FolderPathNode = {
  id: number;
  name: string;
  parent_id: number | null;
};

export function fileCenterFolderPath(folderId: number, folders: readonly FolderPathNode[]): string {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const names: string[] = [];
  const seen = new Set<number>();
  let current = byId.get(folderId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.name);
    current = current.parent_id == null ? undefined : byId.get(current.parent_id);
  }
  return names.join(" > ");
}

export function folderSubtreeIds(folderId: number, folders: readonly FolderPathNode[]): number[] {
  const children = buildChildrenByParentId(folders);
  const ids: number[] = [];
  const seen = new Set<number>();
  const walk = (id: number) => {
    if (seen.has(id)) return;
    seen.add(id);
    ids.push(id);
    for (const child of children.get(id) ?? []) walk(child.id);
  };
  walk(folderId);
  return ids;
}

export type FolderCheckState = "checked" | "partial" | "unchecked";

export function folderCheckState(
  folderId: number,
  folderIds: readonly number[],
  folders: readonly FolderPathNode[],
): FolderCheckState {
  const selected = new Set(folderIds);
  const subtree = folderSubtreeIds(folderId, folders);
  const count = subtree.filter((id) => selected.has(id)).length;
  if (count === 0) return "unchecked";
  if (count === subtree.length) return "checked";
  return "partial";
}

export function toggleFolderSelection(
  folderIds: readonly number[],
  folderId: number,
  folders: readonly FolderPathNode[],
): number[] {
  const selected = new Set(folderIds);
  const subtree = folderSubtreeIds(folderId, folders);
  if (subtree.every((id) => selected.has(id))) {
    for (const id of subtree) selected.delete(id);
  } else {
    for (const id of subtree) selected.add(id);
  }
  return [...selected].sort((a, b) => a - b);
}

export function withFolderSubtrees(folderIds: readonly number[], folders: readonly FolderPathNode[]): number[] {
  const selected = new Set<number>();
  for (const folderId of folderIds) {
    for (const id of folderSubtreeIds(folderId, folders)) selected.add(id);
  }
  return [...selected].sort((a, b) => a - b);
}

export function withoutFolderSubtree(
  folderIds: readonly number[],
  folderId: number,
  folders: readonly FolderPathNode[],
): number[] {
  const drop = new Set(folderSubtreeIds(folderId, folders));
  return folderIds.filter((id) => !drop.has(id));
}

export function folderScopeLabels(
  folderIds: readonly number[],
  folders: readonly FolderPathNode[],
): Array<{ folderId: number; label: string }> {
  const selected = new Set(folderIds);
  const children = buildChildrenByParentId(folders);
  const labels: Array<{ folderId: number; label: string }> = [];

  const holes = (folderId: number): string[] => {
    const names: string[] = [];
    for (const child of children.get(folderId) ?? []) {
      const state = folderCheckState(child.id, folderIds, folders);
      if (state === "unchecked") {
        names.push(child.name);
      } else if (state === "partial") {
        names.push(...holes(child.id));
      }
    }
    return names;
  };

  const visit = (folder: FolderPathNode) => {
    const state = folderCheckState(folder.id, folderIds, folders);
    if (state === "unchecked") {
      for (const child of children.get(folder.id) ?? []) visit(child);
      return;
    }
    if (state === "checked") {
      labels.push({ folderId: folder.id, label: fileCenterFolderPath(folder.id, folders) });
      return;
    }
    if (selected.has(folder.id)) {
      const missing = holes(folder.id);
      const path = fileCenterFolderPath(folder.id, folders);
      labels.push({
        folderId: folder.id,
        label: missing.length > 0 ? `${path}, except ${missing.join(", ")}` : path,
      });
    }
    for (const child of children.get(folder.id) ?? []) {
      const childState = folderCheckState(child.id, folderIds, folders);
      if (childState === "checked" && !selected.has(folder.id)) {
        labels.push({ folderId: child.id, label: fileCenterFolderPath(child.id, folders) });
      } else if (
        childState === "partial" &&
        (!selected.has(child.id) || !selected.has(folder.id))
      ) {
        visit(child);
      }
    }
  };

  for (const root of children.get(null) ?? []) visit(root);

  const known = new Set(folders.map((folder) => folder.id));
  for (const folderId of folderIds) {
    if (!known.has(folderId)) labels.push({ folderId, label: `Folder ${folderId}` });
  }
  return labels;
}

export const UNGROUPED_SCOPE_LABEL = "Not in a folder";

export function exactSearchScopeText(
  folderIds: readonly number[],
  folders: readonly FolderPathNode[],
  includeUngrouped: boolean,
): string {
  const labels = folderScopeLabels(folderIds, folders).map((entry) => entry.label);
  if (includeUngrouped) labels.push(UNGROUPED_SCOPE_LABEL);
  return labels.join(", ");
}

export function fieldsFromComposeTerms(terms: {
  all: string[];
  phrase: string[];
  any: string[];
  none: string[];
}): ExactSearchFields {
  return {
    all: joinExactWords(terms.all),
    phrase: joinExactPhrases(terms.phrase),
    any: joinExactWords(terms.any),
    none: joinExactWords(terms.none),
  };
}

export function hasPositiveExactTerms(fields: ExactSearchFields): boolean {
  return composeExactSearch({ ...fields, none: "" }).length > 0;
}

export function positiveQueryFromExactSearch(raw: string): string {
  const groups = parseExactSearchGroups(raw);
  return [...groups.all, ...groups.phrases, ...groups.any].join(" ");
}

export function fieldsFromSearchBar(query: string, exactMode: boolean): ExactSearchFields {
  if (!exactMode) {
    return { ...EMPTY_EXACT_SEARCH_FIELDS, all: query };
  }
  return parseExactSearch(query);
}
